import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UserPlus, Copy, MessageSquare, Trash2, RefreshCw, CheckCircle2,
  AlertTriangle, Users, ClipboardList, Eye,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PilotDevicesTab({ token }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [pilots, setPilots] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '', pilot_type: 'particulier', email: '', phone: '', ttl_days: 14,
  });
  const [lastCreated, setLastCreated] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, f] = await Promise.all([
        axios.get(`${API}/admin/pilot-devices`, H),
        axios.get(`${API}/admin/pilot-devices/feedback`, H),
      ]);
      setPilots(p.data?.items || []);
      setFeedback(f.data?.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Naam is vereist'); return; }
    setCreating(true);
    try {
      const payload = { name: form.name.trim(),
                        pilot_type: form.pilot_type,
                        ttl_days: Number(form.ttl_days || 14) };
      if (form.email) payload.email = form.email;
      if (form.phone) payload.phone = form.phone;
      const r = await axios.post(`${API}/admin/pilot-devices`, payload, H);
      setLastCreated(r.data);
      setForm({ name: '', pilot_type: 'particulier', email: '', phone: '', ttl_days: 14 });
      toast.success('Pilot aangemaakt');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (pilotId) => {
    if (!window.confirm('Weet u zeker dat u deze pilot wilt intrekken?')) return;
    try {
      await axios.post(`${API}/admin/pilot-devices/${pilotId}/revoke`, {}, H);
      toast.success('Pilot ingetrokken');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} gekopieerd`),
      () => toast.error('Kopiëren mislukt'));
  };

  return (
    <div className="space-y-5" data-testid="pilot-devices-tab">
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm">
        <strong>Pilot Access — Mijn Powersure.</strong> Maak een pilot aan, deel via
        WhatsApp of kopieer de tekst. Pilots krijgen <strong>geen adminrechten</strong>,
        alleen toegang tot <code>/app</code>. Geldig 14 dagen, intrekbaar.
      </div>

      <DemoPilotCard token={token} />

      {/* Create form */}
      <div className="bg-white border border-border rounded-xl p-4 space-y-3"
        data-testid="pilot-create-card">
        <h3 className="font-semibold flex items-center gap-2">
          <UserPlus className="w-4 h-4" /> Nieuwe pilot aanmaken
        </h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <input placeholder="Naam *" value={form.name}
            data-testid="pilot-form-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border border-border rounded-md px-3 py-2 text-sm" />
          <select value={form.pilot_type}
            data-testid="pilot-form-type"
            onChange={(e) => setForm({ ...form, pilot_type: e.target.value })}
            className="border border-border rounded-md px-3 py-2 text-sm">
            <option value="particulier">Particulier</option>
            <option value="MKB">MKB</option>
          </select>
          <input placeholder="E-mail (optioneel)" value={form.email}
            data-testid="pilot-form-email"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="border border-border rounded-md px-3 py-2 text-sm" />
          <input placeholder="Telefoon (voor WhatsApp, optioneel)" value={form.phone}
            data-testid="pilot-form-phone"
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="border border-border rounded-md px-3 py-2 text-sm" />
          <input type="number" placeholder="Geldigheid in dagen"
            value={form.ttl_days} min="1" max="60"
            data-testid="pilot-form-ttl"
            onChange={(e) => setForm({ ...form, ttl_days: e.target.value })}
            className="border border-border rounded-md px-3 py-2 text-sm" />
        </div>
        <button onClick={create} disabled={creating}
          data-testid="pilot-form-submit"
          className="w-full sm:w-auto px-4 py-2 bg-emerald-600 text-white rounded-md text-sm disabled:opacity-50">
          {creating ? 'Bezig...' : 'Pilot aanmaken'}
        </button>
      </div>

      {/* Last created — share panel */}
      {lastCreated && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4 space-y-3"
          data-testid="pilot-share-card">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-blue-900 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Pilot aangemaakt — deel nu
            </h3>
            <button onClick={() => setLastCreated(null)} className="text-xs text-blue-700">
              Sluiten
            </button>
          </div>
          <div className="bg-white rounded-md p-3 space-y-1 text-sm">
            <div><strong>Naam:</strong> {lastCreated.pilot.name}</div>
            <div><strong>Type:</strong> {lastCreated.pilot.pilot_type}</div>
            <div><strong>Pilotcode:</strong> <code className="bg-emerald-50 px-2 py-0.5 rounded font-mono"
              data-testid="pilot-share-code">{lastCreated.pilot.code}</code></div>
            <div><strong>Geldig tot:</strong> {lastCreated.pilot.expires_at?.slice(0, 10)}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs font-medium text-blue-900">Pilotlink:</div>
            <div className="flex gap-1">
              <input readOnly value={lastCreated.link}
                data-testid="pilot-share-link"
                className="flex-1 border border-blue-200 rounded-md px-2 py-1.5 text-xs bg-white" />
              <button onClick={() => copy(lastCreated.link, 'Link')}
                data-testid="pilot-copy-link"
                className="px-3 py-1.5 bg-white border border-blue-300 rounded-md text-xs flex items-center gap-1">
                <Copy className="w-3 h-3" /> Kopieer
              </button>
            </div>
            {/* Surface where this link actually points so the owner can
                instantly tell preview vs production. */}
            {(() => {
              const isPreview = /preview\.emergentagent\.com/i.test(lastCreated.link || '');
              return (
                <div className={`text-[11px] mt-1 ${isPreview ? 'text-amber-800' : 'text-emerald-800'}`}
                  data-testid="pilot-share-link-env">
                  {isPreview
                    ? '⚠️ Deze link wijst naar de preview-omgeving. Klanten kunnen een "Wake up servers"-melding zien. Voor echte pilots: gebruik productie (powersure-worldwide.com).'
                    : '✓ Link wijst naar productie. Klanten zien geen preview-meldingen.'}
                </div>
              );
            })()}
          </div>
          <div className="space-y-1">
            <div className="text-xs font-medium text-blue-900">Berichttekst:</div>
            <textarea readOnly value={lastCreated.share_text}
              data-testid="pilot-share-text" rows={4}
              className="w-full border border-blue-200 rounded-md p-2 text-xs bg-white" />
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <a href={lastCreated.whatsapp_url} target="_blank" rel="noreferrer"
              data-testid="pilot-share-whatsapp"
              onClick={() => axios.post(`${API}/pilot-devices/activity`,
                { event: 'pilot_shared_whatsapp_clicked' },
                { headers: { Authorization: `Bearer ${lastCreated.pilot.pilot_id}` }})
                .catch(() => {})}
              className="px-4 py-2 bg-[#25D366] text-white rounded-md text-sm font-medium flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Verstuur via WhatsApp
            </a>
            <button onClick={() => copy(lastCreated.share_text, 'Tekst')}
              data-testid="pilot-copy-text"
              className="px-4 py-2 bg-white border border-blue-300 rounded-md text-sm flex items-center gap-2">
              <Copy className="w-4 h-4" /> Kopieer tekst
            </button>
          </div>
        </div>
      )}

      {/* Pilots list */}
      <div className="bg-white border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" /> Pilots ({pilots.length})
          </h3>
          <button onClick={load} className="text-xs flex items-center gap-1 text-muted-foreground">
            <RefreshCw className="w-3 h-3" /> Verversen
          </button>
        </div>
        {pilots.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nog geen pilots.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-1">Code</th>
                  <th className="text-left">Naam</th>
                  <th className="text-left">Type</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Events</th>
                  <th className="text-left">Feedback</th>
                  <th className="text-left">Verloopt</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pilots.map((p) => (
                  <tr key={p.pilot_id}
                    data-testid={`pilot-row-${p.pilot_id}`}
                    className="border-b border-border/40">
                    <td className="py-1.5 font-mono text-xs">{p.code}</td>
                    <td>{p.name}</td>
                    <td className="text-xs">{p.pilot_type}</td>
                    <td>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.revoked
                        ? 'bg-red-100 text-red-700'
                        : p.feedback_received ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="text-xs">{p.events_count}</td>
                    <td>{p.feedback_received ? '✓' : '—'}</td>
                    <td className="text-xs text-muted-foreground">{p.expires_at?.slice(0, 10)}</td>
                    <td>
                      {!p.revoked && (
                        <button onClick={() => revoke(p.pilot_id)}
                          data-testid={`pilot-revoke-${p.pilot_id}`}
                          className="text-red-600 hover:bg-red-50 p-1 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feedback */}
      <div className="bg-white border border-border rounded-xl p-4"
        data-testid="pilot-feedback-list">
        <h3 className="font-semibold flex items-center gap-2 mb-3">
          <ClipboardList className="w-4 h-4" /> Pilotfeedback ({feedback.length})
        </h3>
        {feedback.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nog geen feedback ontvangen.</p>
        ) : (
          <div className="space-y-3">
            {feedback.slice(0, 10).map((f) => (
              <div key={f._id} className="border border-border/60 rounded-md p-3 text-sm space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span><strong>{f.pilot_name}</strong> · {f.pilot_type}</span>
                  <span>{f.ts?.slice(0, 16).replace('T', ' ')}</span>
                </div>
                <div className="grid sm:grid-cols-4 gap-2 text-xs">
                  <div>Duidelijkheid: <strong>{f.clarity_score}/10</strong></div>
                  <div>Privacy: <strong>{f.privacy_trust_score}/10</strong></div>
                  <div>Apparaat-add: <strong>{f.device_add_success}</strong></div>
                  <div>Zou gebruiken: <strong>{f.would_use}</strong></div>
                </div>
                {f.unclear_text && <div className="text-xs"><em>Onduidelijk:</em> {f.unclear_text}</div>}
                {f.missing_feature && <div className="text-xs"><em>Mist:</em> {f.missing_feature}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hard safety footer */}
      <div className="text-xs text-muted-foreground flex items-center gap-2">
        <AlertTriangle className="w-3 h-3" />
        Pilots hebben geen admin/customer rechten en kunnen geen apparaten aansturen.
        Live control staat globaal uit.
      </div>
    </div>
  );
}

// ---------- Owner demo-pilot opener -----------------------------------------
// Opens Mijn Powersure als testklant zonder dat owner zelf een pilot hoeft
// aan te maken. Hergebruikt POST /api/admin/demo-pilot/open (find-or-create).
function DemoPilotCard({ token }) {
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState('');
  const [lastSeed, setLastSeed] = useState(null);

    const openDemo = async () => {
      setBusy(true);
      try {
        const r = await axios.post(
          `${API}/admin/demo-pilot/open`, {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const code = r.data?.code;
        const pilotToken = r.data?.token;
        const seed = r.data?.demo_seed || null;
        setLastSeed(seed);
        if (!code || !pilotToken) {
          toast.error('Geen geldige demo-pilot ontvangen');
          return;
        }
        const link = `${window.location.origin}/app/pilot/${code}?t=${pilotToken}`;
        setLastLink(link);
        // Wis alle bestaande tokens in DEZE tab zodat een eventuele oude
        // pilot/customer sessie niet meer kan interfereren met de demo.
        try {
          localStorage.removeItem('powersure_pilot_token');
          localStorage.setItem('powersure_pilot_token', pilotToken);
        } catch (_) { /* localStorage disabled in some browser modes */ }
        // Diagnostische toast met seed-status (helpt productie-validatie)
        const total = seed?.quotes_total_in_db ?? '?';
        if (seed?.errors?.length) {
          toast.error(`Demo seed-fout: ${seed.errors[0]?.error || 'onbekend'}`);
        } else {
          toast.success(`Demo geopend (${total} voorbeeld-offertes klaar)`);
        }
        window.open(link, '_blank', 'noopener,noreferrer');
      } catch (e) {
        toast.error(e?.response?.data?.detail?.error
                    || e?.response?.data?.detail || e.message);
      } finally { setBusy(false); }
    };

  const copyLink = async () => {
    if (!lastLink) return;
    try {
      await navigator.clipboard.writeText(lastLink);
      toast.success('Demo-link gekopieerd');
    } catch { toast.error('Kopiëren mislukt'); }
  };

  return (
    <div
      className="bg-white border border-border rounded-xl p-4 space-y-3"
      data-testid="demo-pilot-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <Eye className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold">Bekijk Mijn Powersure demo</div>
            <div className="text-xs text-muted-foreground">
              Opent de klant-app met voorbeelddata — geen mail, geen betaling,
              geen klantdata. Demo-pilot wordt automatisch beheerd.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={openDemo}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
          data-testid="demo-pilot-open-btn"
        >
          <Eye className="w-4 h-4" />
          {busy ? 'Bezig…' : 'Open demo-app'}
        </button>
      </div>
      {lastLink && (
        <div className="border-t border-border pt-2 space-y-2"
          data-testid="demo-pilot-link-section">
          <div className="text-[11px] text-muted-foreground">
            Demo-link (voor mobiele test of delen met collega):
          </div>
          <div className="flex gap-2">
            <input
              readOnly
              value={lastLink}
              className="flex-1 text-xs border border-border rounded px-2 py-1 font-mono bg-slate-50"
              data-testid="demo-pilot-link"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={copyLink}
              className="text-xs px-2 py-1 bg-white border border-border rounded"
              data-testid="demo-pilot-copy-link"
            >
              Kopieer
            </button>
          </div>
          {lastSeed && (
            <div className="text-[11px] text-slate-500"
              data-testid="demo-pilot-seed-status">
              Status seed: {lastSeed.quotes_total_in_db ?? 0} voorbeeld-offertes
              in database{lastSeed.errors?.length ? ` · fouten: ${lastSeed.errors.length}` : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
